// apps/dashboard/src/app/error.tsx
'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { AlertTriangle, RotateCw, LayoutDashboard } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useBranding, BrandWordmark } from '@test-prep/b2b-branding-client';
import * as Sentry from '@sentry/nextjs';
import { recoverFromChunkError } from '@/lib/chunk-reload';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations('error');
  const { appName } = useBranding();

  useEffect(() => {
    // A stale chunk after a deploy isn't an application fault — reload once and the user
    // lands on the current build instead of reading an error page. Recovering first also
    // keeps deploy churn out of the issue stream.
    if (recoverFromChunkError(error)) return;
    console.error(error);
    // This boundary catches route-level errors, which is the common case;
    // global-error.tsx only fires for root-layout failures. Without this call those
    // errors never reached GlitchTip.
    Sentry.captureException(error);
  }, [error]);

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-6 py-12">
      {/* The error page is still the product. An unbranded slab of centered text
          reads like a proxy/CDN failure — the wordmark says "we know, it's us,
          you're still on Aksademia". */}
      <Link href="/" className="mb-10 text-2xl font-bold tracking-tight text-slate-900">
        <BrandWordmark accentClassName="text-teal-600" />
      </Link>

      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
          <AlertTriangle className="h-6 w-6 text-amber-500" strokeWidth={1.75} />
        </div>

        <p className="font-mono text-xs font-semibold uppercase tracking-widest text-amber-600">
          {t('errorCode')}
        </p>
        <h1 className="mt-2 text-xl font-bold tracking-tight text-slate-900">{t('title')}</h1>
        {/* Capped and centred: a full-bleed paragraph in a 448px card wraps to a ragged
            two-liner whose short second line reads as a misalignment. */}
        <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-balance text-slate-500">
          {t('description')}
        </p>

        {/* Surfaced only when Next actually provides one. It's the single thing that
            makes a user's report actionable — without it a bug report is "it broke". */}
        {error.digest && (
          <p className="mt-4 font-mono text-[11px] text-slate-400">
            {t('reference')}: {error.digest}
          </p>
        )}

        {/* Equal-width, full-bleed on mobile; centred and auto-width from sm up. The
            two actions are the same visual weight apart from colour, so they must be
            the same height and share a baseline — an inline-flex pair inside a
            text-center block does neither reliably. */}
        <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
          <button
            onClick={reset}
            className="inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-teal-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 sm:w-auto"
          >
            <RotateCw className="h-4 w-4" />
            {t('retry')}
          </button>
          <Link
            href="/"
            className="inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition-colors hover:border-teal-300 hover:bg-teal-50/40 sm:w-auto"
          >
            <LayoutDashboard className="h-4 w-4" />
            {t('dashboard')}
          </Link>
        </div>
      </div>

      <p className="mt-8 text-xs text-slate-400" suppressHydrationWarning>
        &copy; {new Date().getFullYear()} {appName}
      </p>
    </main>
  );
}
